import React, { useState, useEffect, useRef, useContext } from "react";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { BASE_URL, RoleEnum } from "../../Constants";
import {
  AppleFilled,
  AppleOutlined,
  LoadingOutlined,
  LoginOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { Modal } from "antd";
import AppContext from "../../AppContext";
import SocialIconRow from "./SocialIconRow";

const PublicEmployeeCard = () => {
  const { logout, profile, cardConfig, companyLogo } = useContext(AppContext);
  const { id, token } = useParams();
  const navigate = useNavigate();
  const cardType = id ? "company" : token ? "person" : "";
  const defaultConfig = {
    companyLogoVisible: cardType === "company" ? true : false,
    profileImageVisible: true,
    nameVisible: true,
    designationVisible: true,
    phoneVisible: true,
    emailVisible: true,
    employeeIdVisible: true,
    companyNameVisible: true,
    qrCodeVisible: true,
    backgroundColor: "#000147",
    useBackgroundImage: true,
    dividerColor: "#81a2fd",
    textStyles: {
      name: {
        color: "#ffffff",
        size: "20",
        bold: true,
        italic: false,
      },
      designation: {
        color: "#ffffff",
        size: "14",
        bold: true,
        italic: false,
      },
      phone: {
        color: "#ffffff",
        size: "12",
        bold: false,
        italic: false,
      },
      email: {
        color: "#ffffff",
        size: "12",
        bold: false,
        italic: false,
      },
      employeeId: {
        color: "#ffffff",
        size: "12",
        bold: false,
        italic: false,
      },
      companyName: {
        color: "#ffffff",
        size: "18",
        bold: true,
        italic: false,
      },
    },
  };
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [employeeId, setEmployeeId] = useState();
  const [error, setError] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [config, setConfig] = useState(null);
  const [companyLogoUrl, setCompanyLogoUrl] = useState(null);
  const [btnLoading, setBtnLoading] = useState({
    apple: false,
    vCard: false,
    samsung: false,
    google: false,
  });
  const [source, setSource] = useState();
  const [isArabic, setIsArabic] = useState(false);
  const [qrCode, setQrCode] = useState();
  const [qrLoading, setQrLoading] = useState(false);

  const fetchEmployeeCard = async (dataParam) => {
    try {
      setQrLoading(true);
      setLoading(true);
      setError(null);
      const response = await axios.get(
        cardType === "company"
          ? `${BASE_URL}/Employee/digital-card/${id}`
          : `${BASE_URL}/PublicEmployee/validate-token/${token}`
      );
      if (cardType === "company") {
        setEmployeeId(id);
        fetchQRCode(id, dataParam);
        setEmployee(response.data.employee);
        setConfig(response.data?.config || cardConfig);
        setCompanyLogoUrl(response.data?.companyLogo || companyLogo);
      } else {
        setEmployeeId(response.data.id);
        fetchQRCode(response.data.id, dataParam);
        setEmployee({
          ...response.data,
          isArabicSupported: response.data.arabicSupport,
          companyName: response.data.company,
          role: response.data.designation,
        });
        setConfig(response.data.digitalCardConfig || defaultConfig);
        setCompanyLogoUrl(response.data.iconUrl || "/future_cards_logo.png");
      }
    } catch (err) {
      if (err.response?.data?.isSubscriptionExpired) {
        showSubscriptionExpiredModal();
      } else {
        setError("Failed to load employee card");
        console.error("Error fetching employee card:", err);
      }
    } finally {
      setQrLoading(false);
      setLoading(false);
    }
  };
  const fetchQRCode = async (
    employee = employeeId,
    sourceVal = "web",
    lng = isArabic ? "ar" : "en"
  ) => {
    try {
      const response = await fetch(
        `${BASE_URL}/wallet/get-qr-code?employeeId=${employee}&isIndividual=${cardType !== "company"
        }&lng=${lng}&source=${sourceVal}`
      );
      if (response.ok) {
        const blob = await response.blob(); // Get the image as a blob
        const qrCodeUrl = URL.createObjectURL(blob); // Create a URL for the image blob
        setQrCode(qrCodeUrl); // Set the QR code URL to display
      } else {
        console.error("Failed to fetch QR code");
      }
    } catch (error) {
      console.error("Error fetching QR code:", error);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const dataParam = params.get("source");
    if (dataParam) setSource(dataParam);
    fetchEmployeeCard(dataParam);
  }, []);

  const showSubscriptionExpiredModal = () => {
    // Standalone Individual cards ("person") have no administrator — offer self-serve renewal.
    // Corporate ("company") cards are unchanged: they renew through their company admin.
    if (cardType !== "company") {
      Modal.error({
        title: "Subscription Expired",
        content:
          "Your Digital Card subscription has expired. Renew it to reactivate your card and sharing links.",
        okText: "Renew now",
        cancelText: "Close",
        okCancel: true,
        onOk: () => {
          // /subscription is behind login; ProtectedRoute stores the target and returns here after sign-in.
          navigate("/subscription");
        },
        onCancel: () => {
          navigate("/");
        },
      });
      return;
    }

    Modal.error({
      title: "Subscription Expired",
      content:
        "Your company's subscription has expired. Please contact your administrator to renew the subscription.",
      okText: "Contact Support",
      onOk: () => {
        // You can add navigation to support page or contact form here
        navigate("/");
      },
    });
  };

  const getTextStyle = (field) => {
    if (!config?.textStyles) return {};
    const textStyle = config.textStyles[field] || {};
    return {
      color: textStyle.color || "#000",
      fontSize: `${textStyle.size}px`,
      fontWeight: textStyle.bold ? "bold" : "normal",
      fontStyle: textStyle.italic ? "italic" : "normal",
      direction: isArabic ? "rtl" : "ltr",
    };
  };

  const handleDownloadVCard = () => {
    setBtnLoading({ ...btnLoading, vCard: true });
    try {
      // Navigate directly to the server URL instead of fetching a blob and
      // re-triggering a client-side download: iOS Safari only hands the
      // response to the native "Add Contact" sheet when it sees a real
      // text/vcard response, not a synthetic blob: URL, and a blob: URL
      // with the download attribute is unreliable there.
      // Apple Contacts and Android need social links written differently in the
      // vCard, so tell the server which one will import it. An iPad reports a
      // Mac user agent, but both import with Apple Contacts.
      const ua = navigator.userAgent || "";
      const platform = /iPhone|iPad|Macintosh/.test(ua) ? "apple" : /Android/.test(ua) ? "other" : "";
      const vCardUrl = `${BASE_URL}/wallet/vCard/generate-pass?employeeId=${employeeId}&isIndividual=${cardType !== "company"
        }&lng=${isArabic ? "ar" : "en"}${platform ? `&platform=${platform}` : ""}`;
      window.location.href = vCardUrl;
    } catch (error) {
      console.error("Error downloading vCard:", error);
    }
    setBtnLoading({ ...btnLoading, vCard: false });
  };

  // const handleDownloadVCard = () => {
  //   if (employee?.digitalID?.vCard) {
  //     // Convert vCard string to base64
  //     const base64VCard = isArabic
  //       ? btoa(unescape(encodeURIComponent(employee.digitalID.vCardArabic)))
  //       : btoa(employee.digitalID.vCard);
  //     const fileName = `${
  //       isArabic
  //         ? `${employee.arabicContent.firstName}_${employee.arabicContent.lastName}.vcf`
  //         : `${employee.firstName}_${employee.lastName}.vcf`
  //     }`;

  //     return (
  //       <div>
  //         <a
  //           href={`data:text/vcard;base64,${base64VCard}`}
  //           download={fileName}
  //           className="mt-6 flex items-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 rounded-lg shadow-md transition-colors"
  //         >
  //           <UserAddOutlined className="w-5 h-5" />
  //           <span>{isArabic ? "إضافة جهة الاتصال" : "Add Contact"} </span>
  //         </a>
  //         <a></a>
  //       </div>
  //     );
  //   }
  //   return null;
  // };

  const getLocalizedContent = (field) => {
    if (
      isArabic &&
      employee?.isArabicSupported &&
      employee?.arabicContent?.[field]
    ) {
      return employee.arabicContent[field];
    }
    return employee?.[field] || "";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900" />
      </div>
    );
  }

  if (error || !employee || !config) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="text-center">
          <p className="text-red-500 mb-2">Failed to load employee card</p>
          <p className="text-sm text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const handleGeneratePass = async () => {
    setBtnLoading({ ...btnLoading, apple: true });
    try {
      // iOS Safari only hands the response to the native "Add to Wallet" sheet when it sees
      // a real navigation to a .pkpass response, not a synthetic blob: URL + <a download> click.
      const passUrl = `${BASE_URL}/wallet/apple/generate-pass?employeeId=${employeeId}&lng=${isArabic ? "ar" : "en"}&isIndividual=${cardType !== "company"
        }`;
      window.location.href = passUrl;
      setTimeout(() => {
        setBtnLoading({ ...btnLoading, apple: false });
      }, 10000);
    } catch (error) {
      console.error("Error:", error);
      setBtnLoading({ ...btnLoading, apple: false });
    }
  };

  const addToGWallet = async () => {
    setBtnLoading({ ...btnLoading, google: true });
    try {
      const response = await axios.post(
        `${BASE_URL}/wallet/google/generate-jwt?employeeId=${employeeId}&isIndividual=${cardType !== "company"
        }&lng=${isArabic ? "ar" : "en"}`
      );

      if (response.data.walletUrl) {
        window.location.href = response.data.walletUrl;
      } else {
        alert("Failed to generate Samsung Wallet ID");
      }
    } catch (error) {
      console.error("Error adding to Samsung Wallet:", error);
    }
    setBtnLoading({ ...btnLoading, google: false });
  };

  const addToSamsungWallet = async () => {
    setBtnLoading({ ...btnLoading, samsung: true });

    try {
      const response = await axios.post(
        `${BASE_URL}/wallet/samsung/generate-jwt?employeeId=${employeeId}&isIndividual=${cardType !== "company"
        }&lng=${isArabic ? "ar" : "en"}`
      );

      if (response.data.walletUrl) {
        window.location.href = response.data.walletUrl;
      } else {
        alert("Failed to generate Samsung Wallet ID");
      }
    } catch (error) {
      console.error("Error adding to Samsung Wallet:", error);
    }
    setBtnLoading({ ...btnLoading, samsung: false });
  };

  const OnLoginClick = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen absolute top-0 left-0 w-screen bg-gray-100">
      <div className="w-full max-w-md mx-auto py-8 px-4">
        {/* Language Switcher */}
        {employee.isArabicSupported && (
          <div className="flex justify-center mb-6">
            <div className="inline-flex rounded-lg p-1 bg-white shadow-md">
              <button
                onClick={() => {
                  setIsArabic(false);
                  fetchQRCode(employeeId, source, "en");
                }}
                className={`px-6 py-2 rounded-md transition-colors ${!isArabic
                    ? "bg-blue-500 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                  }`}
              >
                EN
              </button>
              <button
                onClick={() => {
                  setIsArabic(true);
                  fetchQRCode(employeeId, source, "ar");
                }}
                className={`px-6 py-2 rounded-md transition-colors ${isArabic
                    ? "bg-blue-500 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                  }`}
              >
                AR
              </button>
            </div>
          </div>
        )}

        {/* Employee Card */}
        <div
          style={{
            backgroundColor: !config.useBackgroundImage
              ? config.backgroundColor
              : !config.backgroundImageUrl
                ? "darkblue"
                : undefined,
            backgroundImage:
              config.useBackgroundImage && config.backgroundImageUrl
                ? `url(${config.backgroundImageUrl})`
                : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          className="bg-white rounded-lg shadow-lg overflow-hidden relative"
        >
          {/* Overlay div */}
          <div
            className="absolute inset-0 bg-black opacity-40"
            style={{
              backgroundColor: !config.useBackgroundImage
                ? config.backgroundColor
                : undefined,
            }}
          ></div>
          <div className="relative aspect-[2.4] w-full">
            {config.companyLogoVisible && companyLogoUrl && (
              <div className="flex justify-center mt-14">
                <img
                  src={companyLogoUrl}
                  alt="Company Logo"
                  className={`object-contain pb-4 h-14`}
                />
              </div>
            )}
          </div>

          <div className="relative px-6 pb-6">
            {config.profileImageVisible && (
              <div className="absolute -top-16 left-1/2 -translate-x-1/2">
                <img
                  src={employee.profileUrl || (employee.gender === "Female" && cardType !== "company" ? `/user_placeholder_women.png` : `/user-placeholder.png`)}
                  alt="Profile"
                  className="w-32 h-32 rounded-full border-4 border-white object-cover"
                />
              </div>
            )}

            <div className="pt-20 text-center">
              {config?.nameVisible && (
                <h3 style={getTextStyle("name")} className="mb-1">
                  {`${getLocalizedContent("firstName")} ${getLocalizedContent(
                    "lastName"
                  )}`}
                </h3>
              )}
              {config.designationVisible && (
                <p style={getTextStyle("designation")}>
                  {getLocalizedContent("role")}
                </p>
              )}
            </div>

            <div className="mt-6 space-y-1 text-center">
              {config.phoneVisible && (
                <p style={getTextStyle("phone")}>{employee.phone}</p>
              )}
              {config.emailVisible && (
                <p style={getTextStyle("email")}>{employee.email}</p>
              )}
              {config.employeeIdVisible && (
                <p style={getTextStyle("employeeId")}>{employee.employeeId}</p>
              )}
            </div>

            <div
              className="mt-6 w-60 flex mx-auto border-t"
              style={{
                borderTop: `2px solid ${config.dividerColor}`,
              }}
            />

            <div className="pt-4 mb-4">
              {config.companyNameVisible && (
                <p style={getTextStyle("companyName")} className="text-center">
                  {getLocalizedContent("companyName")}
                </p>
              )}
            </div>

            <SocialIconRow
              links={employee.socialLinks}
              color={getTextStyle("companyName").color || "#ffffff"}
              className="mb-4"
            />

            {config.qrCodeVisible && qrCode && (
              <div className="flex justify-center py-6">
                <img
                  src={qrCode}
                  alt="QR Code"
                  className="w-32 h-32 object-contain"
                />
              </div>
            )}
          </div>
        </div>

        {source === "card" ? (
          <div className="mt-6 grid gap-2 grid-cols-2 items-center">
            <button
              onClick={handleDownloadVCard}
              className="col-span-2 w-fit mx-auto flex items-center justify-center gap-2 px-6 py-2 bg-black text-white hover:bg-gray-800 rounded-lg shadow-md transition-colors"
            >
              {btnLoading.vCard && (
                <LoadingOutlined style={{ color: "white" }} />
              )}
              <UserAddOutlined className="w-5 h-5" />
              <span>{isArabic ? "إضافة جهة الاتصال" : "Add Contact"}</span>
            </button>
          </div>
        ) : (
          <div className="mt-6 grid gap-2 grid-cols-2 items-center">
            <button
              onClick={handleDownloadVCard}
              className=" flex items-center justify-center gap-2 px-6 py-2 bg-black text-white hover:bg-gray-800 rounded-lg shadow-md transition-colors"
            >
              {" "}
              {btnLoading.vCard && (
                <LoadingOutlined style={{ color: "white" }} />
              )}
              <UserAddOutlined className="w-5 h-5" />
              <span>{isArabic ? "إضافة جهة الاتصال" : "Add Contact"}</span>
            </button>{" "}
            <div>
              <button
                onClick={handleGeneratePass}
                className="flex items-center justify-center gap-2 px-6 py-2 font-medium w-full bg-black text-white hover:bg-gray-800 rounded-lg shadow-md transition-colors"
              >
                {btnLoading.apple && (
                  <LoadingOutlined style={{ color: "white" }} />
                )}
                <AppleFilled height={14} />
                <span>{isArabic ? " محفظة أبل" : "Apple Wallet"}</span>
              </button>
            </div>
            <div>
              <button
                onClick={addToSamsungWallet}
                className="flex items-center justify-center gap-2 px-6 py-1 font-medium w-full bg-black text-white hover:bg-gray-800 rounded-lg shadow-md transition-colors"
              >
                {btnLoading.samsung && (
                  <LoadingOutlined style={{ color: "white" }} />
                )}
                <img
                  src="https://us-cdn-gpp.mcsvc.samsung.com/lib/wallet-card.png"
                  alt=""
                />
                <img
                  src="https://us-rd.mcsvc.samsung.com/statistics/impression/addtowlt?ep=C50C3754FEB24833B30C10B275BB6AB8;cc=GC;ii=1287098641027170318;co=4111756569175138240;cp=1288017491089625089;si=24;pg=101212967375212546;pi=Aqz68EBXSx6Mv9jsaZxzaA;tp=4111774992902138240;li=0"
                  style={{ width: "1px", height: "1px" }}
                  alt=""
                />
              </button>
            </div>
            {/* <SamsungWalletButton cdata={""} /> */}
            <div>
              <button
                onClick={addToGWallet}
                className="col-span-2 flex h-10 px-3 items-center justify-center w-full gap-2 bg-black hover:bg-gray-800 rounded-lg shadow-md transition-colors"
              >
                {btnLoading.google && (
                  <LoadingOutlined style={{ color: "white" }} />
                )}
                <img
                  src="/g_wallet-btn.svg"
                  className="max-h-8 w-full bg-transparent max-w-[150px] object-contain"
                />
              </button>
            </div>
          </div>
        )}
        {source === "email" ? (
          <div className="w-full flex justify-center mt-6">
            <button
              onClick={OnLoginClick}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg shadow-md flex items-center space-x-2 transition-all duration-300 ease-in-out hover:shadow-lg hover:-translate-y-0.5"
            >
              <LoginOutlined height={20} />
              <span>Log in to Edit Your Card</span>
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PublicEmployeeCard;
